import * as React from 'react'

import { renderers } from '@markdoc/markdoc'
import type { HeadersFunction, LoaderFunctionArgs, MetaFunction } from 'react-router'
import { useLoaderData } from 'react-router'

import ContentImage from '@/components/content/ContentImage'
import Cta from '@/components/content/Cta'
import SiteFooter from '@/components/SiteFooter'
import SiteHeader from '@/components/SiteHeader'
import { findEvent } from '@/models/event.server'
import { pageHelper } from '@/request'
import { formatLong } from '@/utils/date'
import { eventCta, eventLabel } from '@/utils/events'
import { EventCategory } from '@/utils/types'

type LoaderData = typeof loader

export async function loader({ params, url }: LoaderFunctionArgs) {
  const page = pageHelper(url)
  const { slug } = params

  if (!slug) {
    throw new Response('Not found', { status: 404 })
  }

  const event = findEvent(slug)

  if (!event) {
    throw new Response('Not found', { status: 404 })
  }

  const { root } = page

  return {
    event: event,
    root: root
  }
}

export const headers: HeadersFunction = ({ parentHeaders }) => ({
  'Cache-Control': parentHeaders.get('Cache-Control') ?? ''
})

export const meta: MetaFunction<LoaderData> = ({ loaderData }) => {
  if (!loaderData) return []

  const { event, root } = loaderData

  const title = `${event.title} — PlanetScale Events`
  const description = event.meta.description
  const image = `${root}${event.meta.image}`

  return [
    { title },
    { name: 'description', content: description },
    { property: 'og:url', content: event.meta.canonical },
    { property: 'og:type', content: 'article' },
    { property: 'og:title', content: title },
    { property: 'og:image', content: image },
    { property: 'og:description', content: description },
    { property: 'twitter:card', content: 'summary_large_image' },
    { property: 'twitter:site', content: '@PlanetScale' },
    { property: 'twitter:creator', content: '@PlanetScale' },
    { property: 'twitter:url', content: event.meta.canonical },
    { property: 'twitter:title', content: title },
    { property: 'twitter:description', content: description },
    { property: 'twitter:image', content: image },
    { tagName: 'link', rel: 'canonical', href: event.meta.canonical }
  ]
}

export default function EventDetail() {
  const { event } = useLoaderData<LoaderData>()

  const eventDate = new Date(event.createdAt)
  // oxlint-disable-next-line react/purity -- whether the event is past intentionally depends on the current time
  const isPastEvent = eventDate < new Date()

  return (
    <>
      <SiteHeader />

      <main className='container mb-6 max-w-7xl px-3 sm:px-5 lg:px-12'>
        <article>
          <header className='mb-2'>
            <div className='mb-1 flex flex-wrap items-center justify-between gap-y-2'>
              <div className='flex flex-wrap gap-x-2 gap-y-1'>
                <h1 className='mb-0 ml-0 border-none pl-0 indent-0 font-semibold'>
                  <span className='bg-gray-800 box-decoration-clone px-1 py-sm text-white dark:bg-gray-100 dark:text-black'>
                    {event.title}
                  </span>
                </h1>
                <span className='font-medium text-green'>{eventLabel(event.category)}</span>
              </div>

              {!isPastEvent && <EventLink category={event.category} url={event.url} />}
            </div>

            <div className='mb-4 flex flex-col lg:flex-row lg:items-center lg:justify-between'>
              <div className='flex space-x-1 text-secondary'>
                <time dateTime={event.createdAt} className='mb-1 lg:mb-0'>
                  {formatLong(event.createdAt)}
                </time>
                {event.location && (
                  <>
                    <span>&middot;</span>
                    <span className='mb-1 lg:mb-0'>{event.location}</span>
                  </>
                )}
              </div>
            </div>
          </header>

          <div className='prose prose-lg dark:prose-invert max-w-none'>
            {renderers.react(event.body, React, {
              components: {
                ContentImage,
                Cta
              }
            })}
          </div>
        </article>
      </main>

      <SiteFooter />
    </>
  )
}

function EventLink({ category, url }: { category: EventCategory; url: string }) {
  return (
    <a
      className='inline-flex h-5 items-center justify-center border border-orange-500 bg-white px-2 font-semibold text-orange dark:bg-gray-900'
      href={url}
      rel='noopener noreferrer'
      target='_blank'
    >
      {eventCta(category)}
    </a>
  )
}
